//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import angular, { IPromise, IQService, IWindowService } from 'angular';

import { crl8 } from 'app/curalate.globals';
import {
  IRequestRightsOAuthParams,
  getInstagramOauthUrl,
} from 'rights-request-oauth-landing/model';

import {
  IOAuthLandingPagePostMessage,
  OAuthLandingPageMessageType,
  isOAuthMessage,
} from '../../../../rights-request-oauth-landing/models/PostMessages';
import { SessionVarsService } from '../../../services/session-vars-service';
import { PostMessageListener } from '../../../utils/PostMessageListener';

interface IOAuthModalListener {
  /**
   * Promise that resolves when the user has passed the oauth check.
   * Or, will be rejected if the user ch
   */
  rightsInitiated: IPromise<boolean>;

  /**
   * Close the listener.
   */
  close: () => void;
}

export class UniversalImageActionsRightsRequestOAuthService {
  constructor(
    private readonly $q: IQService,
    private readonly $window: IWindowService,
    private readonly sessionVarsService: SessionVarsService,
  ) {}

  public requestRightsWithOAuthCheck(
    params: IRequestRightsOAuthParams,
  ): IOAuthModalListener {
    const resolution = this.$q.defer<boolean>();
    const listener = new PostMessageListener<IOAuthLandingPagePostMessage>(
      this.$window,
      this.$window.location.origin,
      // filter out other postMessage noise, and unrelated instances.
      (msg) => (isOAuthMessage(msg) ? msg : undefined),
    );

    function close() {
      listener.detach();
    }
    listener.attach();
    listener.onMessage((data: IOAuthLandingPagePostMessage) => {
      switch (data.type) {
        case OAuthLandingPageMessageType.OAuthCheckPassed:
          resolution.resolve(true);
          close();
          break;
        case OAuthLandingPageMessageType.OAuthCancelled:
          resolution.resolve(false);
          close();
          break;
        case OAuthLandingPageMessageType.OAuthCheckFailed:
          crl8.logger.cloud.error(
            `RIGHTS REQUEST OAUTH FAILURE - ${data.error}`,
          );
          break;
      }
    });

    // This will be obviated once we no longer feature flag checking which account the user is oauthed with
    const stateParams = new URLSearchParams({
      state: JSON.stringify(params),
    });

    const redirectUrl = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.NonAtMentionedRightsRequestsDisplayApi,
    )
      ? getInstagramOauthUrl(params)
      : `${location.origin}/rights-request-oauth-landing?${stateParams.toString()}`;

    // Start an implicit OAuth flow to instagram, redirecting to our
    // rights request oauth landing after to continue our user-account
    // checking logic.
    this.$window.open(
      redirectUrl,
      'targetWindow',
      'toolbar = no, location = no,status=no,menubar=no,' +
        'scrollbars=yes,resizable=yes,width=750,height=690,left=200,top=100',
    );

    return {
      rightsInitiated: resolution.promise,
      close,
    };
  }
}

angular
  .module('curalate')
  .service('UniversalImageActionsRightsRequestOAuthService', [
    '$q',
    '$window',
    'SessionVarsService',
    UniversalImageActionsRightsRequestOAuthService,
  ]);
